import { ChevronLeft, Clock, DollarSign, Home, TrendingDown, TrendingUp } from 'lucide-react'
import { Card } from '@/components/ui/card'

interface MarketSnapshotScreenProps {
  onBack: () => void
}

const nationalStats = {
  medianPrice: '$825,000',
  annualChange: '+3.2%',
  salesVolume: '6,842',
  daysToSell: '38',
}

const regionalData = [
  {
    region: 'Auckland',
    median: '$1,020,000',
    change: '+2.1%',
    positive: true,
    volume: 2156,
    days: 34,
  },
  {
    region: 'Wellington',
    median: '$845,000',
    change: '+1.5%',
    positive: true,
    volume: 892,
    days: 32,
  },
  {
    region: 'Canterbury',
    median: '$695,000',
    change: '-0.8%',
    positive: false,
    volume: 1124,
    days: 36,
  },
  { region: 'Waikato', median: '$745,000', change: '+2.8%', positive: true, volume: 687, days: 38 },
  {
    region: 'Bay of Plenty',
    median: '$820,000',
    change: '+1.2%',
    positive: true,
    volume: 423,
    days: 35,
  },
  { region: 'Otago', median: '$615,000', change: '-0.3%', positive: false, volume: 312, days: 42 },
  {
    region: "Hawke's Bay",
    median: '$685,000',
    change: '+0.5%',
    positive: true,
    volume: 234,
    days: 39,
  },
  {
    region: 'Manawatū-Whanganui',
    median: '$520,000',
    change: '+1.8%',
    positive: true,
    volume: 198,
    days: 44,
  },
]

export function MarketSnapshotScreen({ onBack }: MarketSnapshotScreenProps) {
  return (
    <div className="flex flex-col min-h-full pb-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-primary mb-4 hover:underline"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-xl font-semibold text-foreground mb-2">NZ Property Market</h1>
      <p className="text-sm text-muted-foreground mb-6">Latest REINZ market data</p>

      {/* National Overview */}
      <Card className="p-4 mb-6 bg-gradient-reinz text-primary-foreground">
        <h2 className="text-sm font-medium opacity-80 mb-3">National Overview</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">{nationalStats.medianPrice}</p>
            <p className="text-xs opacity-70">Median Price</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-accent">{nationalStats.annualChange}</p>
            <p className="text-xs opacity-70">Annual Change</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{nationalStats.salesVolume}</p>
            <p className="text-xs opacity-70">Monthly Sales</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{nationalStats.daysToSell}</p>
            <p className="text-xs opacity-70">Median Days to Sell</p>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <DollarSign className="w-3 h-3" /> Median Price
        </span>
        <span className="flex items-center gap-1">
          <Home className="w-3 h-3" /> Sales
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" /> Days
        </span>
      </div>

      {/* Regional Breakdown */}
      <h2 className="text-sm font-semibold text-foreground mb-3">By Region</h2>
      <div className="space-y-3">
        {regionalData.map((region) => (
          <Card key={region.region} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-foreground">{region.region}</h3>
              <span
                className={`flex items-center gap-1 text-sm font-medium ${
                  region.positive ? 'text-emerald-600' : 'text-red-500'
                }`}
              >
                {region.positive ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                {region.change}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="font-semibold text-foreground">{region.median}</p>
                <p className="text-xs text-muted-foreground">Median</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">{region.volume}</p>
                <p className="text-xs text-muted-foreground">Sales/mo</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">{region.days}</p>
                <p className="text-xs text-muted-foreground">Days</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Disclaimer */}
      <Card className="p-4 bg-secondary/50 mt-6">
        <p className="text-xs text-muted-foreground text-center">
          Data provided by REINZ for informational purposes only. Past performance is not indicative
          of future results. Updated monthly.
        </p>
      </Card>
    </div>
  )
}
